import { useState } from "react";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import { motion } from "framer-motion";
import { Search, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Link } from "react-router-dom";

const faqCategories = [
  {
    title: "Acquisitions & Enquiries",
    items: [
      {
        q: "How do I acquire an artwork?",
        a: "All works are offered via private enquiry. Use the 'Request a Quote' button on any artwork, or the Quote Builder for a bespoke commission, and our team will respond with availability and details.",
      },
      {
        q: "Why are no prices displayed?",
        a: "As a fine art gallery, every acquisition begins with a conversation. Pricing depends on the work, edition, substrate, size, and framing — we prepare a tailored proposal for each enquiry.",
      },
      {
        q: "Do you work with galleries, designers and trade?",
        a: "Absolutely. We have a dedicated trade program for galleries, retailers, and interior designers. Apply for a trade account through our Trade Enquiries page.",
      },
      {
        q: "Can I commission a bespoke piece?",
        a: "Yes. Colin accepts a limited number of commissions each year. Visit our Commissions page or start a quote to tell us about your space and vision.",
      },
    ],
  },
  {
    title: "Artwork & Authenticity",
    items: [
      {
        q: "Are all artworks authentic?",
        a: "Yes. Every artwork is created by Elder Colin Isaacs and comes with a signed Certificate of Authenticity verifying the artwork's origin and provenance.",
      },
      {
        q: "What is the difference between originals and prints?",
        a: "Originals are one-of-a-kind pieces created directly by the artist. Limited edition prints are high-quality reproductions on archival paper or canvas, produced in numbered runs to maintain collectible value.",
      },
      {
        q: "What does 'Limited Edition' mean?",
        a: "Limited edition prints are produced in a fixed quantity. Once all editions are sold, no more will be produced. Each print is numbered (e.g., 12/100) and includes a certificate.",
      },
      {
        q: "Can I get professional framing?",
        a: "Yes. Mention framing in your enquiry or quote request and our team will include custom framing options and pricing for your artwork's exact dimensions.",
      },
    ],
  },
  {
    title: "Shipping & Delivery",
    items: [
      {
        q: "Where do you ship to?",
        a: "We ship worldwide. Australian domestic orders typically arrive in 5-7 business days. International shipping times vary by destination, usually 10-21 business days.",
      },
      {
        q: "Is shipping free?",
        a: "Shipping is arranged individually for each acquisition and included in your quote. All artworks are sent insured, with white-glove options available for large or framed works.",
      },
      {
        q: "How are artworks packaged?",
        a: "All artworks are carefully packaged using acid-free materials, protective corners, and sturdy shipping tubes or flat boxes depending on size. We insure all shipments against damage.",
      },
    ],
  },
  {
    title: "Returns & Refunds",
    items: [
      {
        q: "What is your return policy?",
        a: "We offer a 14-day return policy for undamaged items in their original packaging. Please contact us to initiate a return. Custom or framed orders may not be eligible for return.",
      },
      {
        q: "What if my artwork arrives damaged?",
        a: "All shipments are insured. If your artwork arrives damaged, please photograph the damage and contact us within 48 hours. We will arrange a replacement or full refund.",
      },
    ],
  },
  {
    title: "About the Art & Artist",
    items: [
      {
        q: "Who is Colin Isaacs?",
        a: "Colin Isaacs is a respected Aboriginal Elder — a Gadigal and Bidjigal man and lifelong artist. After 45 years of service to community, he has returned to Country to focus on his art, sharing the stories and traditions of Australia's oldest continuous culture.",
      },
      {
        q: "What does the art represent?",
        a: "Each artwork carries deep cultural significance, often depicting Dreamtime stories, connections to Country, and the relationship between people and land. The cultural story behind each piece is shared on its product page.",
      },
      {
        q: "How does purchasing support community?",
        a: "A portion of proceeds supports Indigenous community programs and cultural preservation. Every purchase directly contributes to keeping Australia's oldest continuous culture alive.",
      },
    ],
  },
];

const FAQPage = () => {
  const [search, setSearch] = useState("");

  const filtered = search.trim()
    ? faqCategories
        .map((cat) => ({
          ...cat,
          items: cat.items.filter(
            (item) =>
              item.q.toLowerCase().includes(search.toLowerCase()) ||
              item.a.toLowerCase().includes(search.toLowerCase())
          ),
        }))
        .filter((cat) => cat.items.length > 0)
    : faqCategories;

  return (
    <div className="min-h-screen" data-testid="faq-page">
      <Navigation />

      <section className="pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs items={[{ label: "FAQ" }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl md:text-4xl font-brand mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Everything you need to know about our artworks, acquisitions, and
              shipping.
            </p>
          </motion.div>

          {/* Search */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 py-6 rounded-xl text-base"
              data-testid="faq-search-input"
            />
          </div>

          {/* FAQ Sections */}
          {filtered.length > 0 ? (
            <div className="space-y-10">
              {filtered.map((category, catIdx) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: catIdx * 0.05 }}
                >
                  <h2 className="text-lg font-medium mb-4 text-primary">
                    {category.title}
                  </h2>
                  <Accordion type="single" collapsible className="w-full">
                    {category.items.map((item, idx) => (
                      <AccordionItem
                        key={idx}
                        value={`${catIdx}-${idx}`}
                        className="border-b"
                      >
                        <AccordionTrigger className="text-left hover:text-primary">
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground leading-relaxed">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No questions match your search. Try different keywords.</p>
            </div>
          )}

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center p-8 bg-muted/30 rounded-2xl"
          >
            <MessageCircle className="h-10 w-10 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-medium mb-2">Still have questions?</h3>
            <p className="text-muted-foreground mb-6">
              Our team is happy to help with any questions about our artworks or
              ordering process.
            </p>
            <Link to="/quote-request">
              <Button className="rounded-full px-8" data-testid="faq-contact-btn">
                Get in Touch
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
